/* =============================================================================
   Buy to Let / Complex BTL calculator — pure functions, no React.
============================================================================= */

const num = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const n = parseFloat(String(v ?? "").replace(/[£,%\s]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const firstPositive = (...vals) => {
  for (const v of vals) {
    const n = num(v);
    if (n > 0) return n;
  }
  return 0;
};

/** Decimal-safe rounding (1.005 → 1.01), unlike Math.round(n * 100) / 100. */
export const roundTo = (n, dp) => {
  const v = num(n);
  return Number(`${Math.round(Number(`${v}e${dp}`))}e-${dp}`);
};

export const roundPenny = (n) => roundTo(n, 2);

/* ----------------------------- Mortgage & LTV ----------------------------- */

export function mortgageFromLtv(propertyValue, ltvPercent) {
  const pv = num(propertyValue);
  const mortgageAmount = roundPenny((pv * num(ltvPercent)) / 100);
  return { mortgageAmount, depositAmount: roundPenny(pv - mortgageAmount) };
}

/**
 * LTV used for product checks: the dropdown value when the user picked one,
 * otherwise mortgage ÷ property value. Always rounded to 2 dp so that
 * 137500 / 250000 (55.00000000000001) compares as 55.
 */
export function ltvForChecks({ selectedLtv, propertyValue, mortgageAmount }) {
  const selected = num(selectedLtv);
  if (selected > 0) return roundTo(selected, 2);
  const pv = num(propertyValue);
  return pv > 0 ? roundTo((num(mortgageAmount) / pv) * 100, 2) : 0;
}

/* ------------------------------- Terms ------------------------------------ */

export const termMonths = (years) => Math.max(1, Math.round(num(years) * 12));

/* --------------------------- Arrangement fee ------------------------------ */

const MIN_FEE_KEYS = ["min_arrangement_fee", "minimum_fee", "min_fee", "arrangement_min_fee", "product_min_fee"];

/**
 * Arrangement fee = fixed £ fee + % fee, where % fee = max(mortgage × fee% ÷ 100, minimum fee).
 * The product stores one fee: product_fee_type "Pound" (fixed) or "Percentage" (% of mortgage).
 * Calculated on the mortgage amount before the fee is added.
 */
export function arrangementFee(product, mortgageAmount) {
  const p = product || {};
  const type = String(p.product_fee_type || p.arrangement_fee_type || p.fee_type || "").trim().toLowerCase();
  const amount = num(firstPositive(p.product_fee_amount, p.arrangement_fee_amount, p.product_fee));
  const percentField = firstPositive(p.product_fee_percent, p.arrangement_fee_percent, p.fee_percent);
  const minimumFee = firstPositive(...MIN_FEE_KEYS.map((k) => p[k]));

  const isPercent = /percent|%|min/.test(type);
  const isPound = /pound|£|amount|fixed|gbp/.test(type);

  let fixedFee = 0;
  let feePercent = 0;
  if (isPercent) feePercent = percentField || amount;
  else if (isPound) fixedFee = amount;
  else if (percentField > 0) feePercent = percentField;
  else fixedFee = amount;

  const mort = num(mortgageAmount);
  const percentFee = feePercent > 0 || (isPercent && minimumFee > 0)
    ? Math.max((mort * feePercent) / 100, minimumFee)
    : 0;

  return { fixedFee, percentFee, feePercent, minimumFee, arrangementFee: Math.max(0, fixedFee + percentFee) };
}

/* ------------------------------ Payments ---------------------------------- */

export const interestOnlyPayment = (loan, ratePercent) => (num(loan) * num(ratePercent)) / 100 / 12;

export function amortisedPayment(loan, ratePercent, months) {
  const principal = num(loan);
  const n = Math.max(1, months);
  if (principal <= 0) return 0;
  const r = num(ratePercent) / 100 / 12;
  if (r === 0) return principal / n;
  const power = Math.pow(1 + r, n);
  return (principal * r * power) / (power - 1);
}

/** Interest paid and balance left after `months` of repayments. */
export function repaymentOverProductTerm(loan, ratePercent, monthlyPayment, months) {
  const r = num(ratePercent) / 100 / 12;
  let remainingBalance = num(loan);
  let interestPaid = 0;
  for (let i = 0; i < months; i++) {
    const interest = remainingBalance * r;
    interestPaid += interest;
    remainingBalance -= monthlyPayment - interest;
    if (remainingBalance <= 0) {
      remainingBalance = 0;
      break;
    }
  }
  return { interestPaid, remainingBalance };
}

/* ---------------------------- Assessed rent ------------------------------- */

const AVERAGE_WEEKS_FIELDS = {
  "Holiday let": "holiday_lets_avg_weeks",
  "Short term let": "short_term_lets_avg_weeks",
};

export const isWeeksBasedPropertyType = (propertyType) => Boolean(AVERAGE_WEEKS_FIELDS[propertyType]);

export function productAverageWeeks(product, propertyType) {
  const field = AVERAGE_WEEKS_FIELDS[propertyType];
  return field ? num(product?.[field]) : 0;
}

export function assessedMonthlyRent(estimatedMonthlyRent, propertyType, product) {
  const rent = num(estimatedMonthlyRent);
  const averageWeeks = productAverageWeeks(product, propertyType);
  if (averageWeeks > 0) {
    const weeklyRent = (rent * 12) / 52;
    return { assessedRent: (weeklyRent * averageWeeks) / 12, averageWeeks };
  }
  return { assessedRent: rent, averageWeeks: 0 };
}

/* --------------------------------- DSC ------------------------------------ */

export function requiredDsc(product, { borrowerType, taxpayerType } = {}) {
  const borrower = String(borrowerType || "");
  const isIndividual = /individual|partnership/i.test(borrower) && !/ltd/i.test(borrower);
  const isHigherRate = /higher|140/i.test(String(taxpayerType || ""));
  return isIndividual && isHigherRate
    ? num(product?.high_taxpayer_dsc)
    : num(product?.basic_taxpayer_dsc_percent);
}

/** Round up to the next pound without float noise turning 1000.0000001 into 1001. */
const ceilPound = (n) => Math.ceil(roundTo(n, 6));

export function minimumRent({ stressedPayment, required, averageWeeks }) {
  let monthly = (num(stressedPayment) * num(required)) / 100;
  if (averageWeeks > 0) monthly = (monthly * 52) / averageWeeks;
  const minimumMonthlyRent = ceilPound(monthly);
  return { minimumMonthlyRent, weeklyEquivalent: (minimumMonthlyRent * 12) / 52 };
}

/**
 * DSC for Interest Only and Repayment, stressed at the product's ICR rate.
 * `termMonthsTotal` must be the same term used for the Repayment monthly payment.
 */
export function calculateDsc(product, { loanIncludingFee, termMonthsTotal, estimatedRent, propertyType, borrowerType, taxpayerType }) {
  const icrRate = num(product?.icr_rate);
  const required = requiredDsc(product, { borrowerType, taxpayerType });
  const { assessedRent, averageWeeks } = assessedMonthlyRent(estimatedRent, propertyType, product);

  const missing = [];
  if (!(icrRate > 0)) missing.push("icr_rate");
  if (!(required > 0)) missing.push(/higher/i.test(String(taxpayerType || "")) ? "high_taxpayer_dsc" : "basic_taxpayer_dsc_percent");

  const showMinimumRent = isWeeksBasedPropertyType(propertyType);

  const mode = (stressedPayment) => {
    const dscPercent = stressedPayment > 0 ? (assessedRent / stressedPayment) * 100 : 0;
    const passes = missing.length === 0 && dscPercent >= required;
    return {
      stressedPayment,
      dscPercent,
      passes,
      minimumRent: !passes && showMinimumRent && missing.length === 0
        ? minimumRent({ stressedPayment, required, averageWeeks })
        : null,
    };
  };

  const interestOnly = mode(interestOnlyPayment(loanIncludingFee, icrRate));
  const repayment = mode(amortisedPayment(loanIncludingFee, icrRate, termMonthsTotal));

  return {
    icrRate,
    required,
    assessedRent,
    averageWeeks,
    missing,
    interestOnly,
    repayment,
    passesAny: interestOnly.passes || repayment.passes,
  };
}

/* ---------------------------- Product checks ------------------------------ */

export function passesLtvCheck(productMaxLtv, ltv) {
  const max = num(productMaxLtv);
  return !(max > 0) || max >= num(ltv);
}

export function passesLoanSizeCheck(product, mortgageAmount) {
  const mort = num(mortgageAmount);
  const min = num(product?.min_loan);
  const max = num(product?.max_loan);
  return (!(min > 0) || mort >= min) && (!(max > 0) || mort <= max);
}

const MAX_UNITS_FIELDS = {
  MUFB: "mufb_max_units",
  MPOT: "mpot_max_units",
  Portfolio: "portfolio_max_units",
};

/**
 * HMO rooms / MUFB, MPOT, Portfolio units. Only applies when the user entered a value,
 * and only to products of the chosen type: other property types stay listed (sorted below).
 * MPOT and Portfolio products often store max units as 0 alongside their type flags;
 * 0 there means no stated limit.
 */
export function passesPropertySizeCheck(product, { propertyType, rooms, units, matchesType }) {
  if (!matchesType) return true;

  if (propertyType === "HMO") {
    const entered = num(rooms);
    if (!(entered > 0)) return true;
    const max = num(product?.hmo_max_rooms);
    return max > 0 && entered <= max;
  }

  const field = MAX_UNITS_FIELDS[propertyType];
  if (!field) return true;
  const entered = num(units);
  if (!(entered > 0)) return true;
  const max = num(product?.[field]);
  if (propertyType === "MUFB") return max > 0 && entered <= max;
  return !(max > 0) || entered <= max;
}
